/**
 * @file DirectusForm.jsx
 * @description A dynamic form component for Directus collections.
 * This component fetches collection schema and renders a form with appropriate fields.
 * It supports creating and updating items, and provides options for layout and customization.
 */

import { Loader2, Plus, Save } from "lucide-react";

import { Button } from "ui/components/button";
import DirectusFieldRenderer from "./field-renderer";
import { PortalMount } from "components";
import React from "react";
import { UI_ACTION_PORTAL_ID } from "utils";
import { useDirectusForm } from "../hooks/useDirectusForm.js";
import { EvtHeader } from "components/header";

// Loading skeleton for form fields
const FormSkeleton = ({ fields = 5 }) => (
  <div className="space-y-6">
    {Array.from({ length: fields }).map((_, i) => (
      <div key={i} className="space-y-2">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 animate-pulse" />
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
      </div>
    ))}
  </div>
);

// Error display component
const FormError = ({ error, onRetry }) => (
  <div className="p-6 border border-red-200 dark:border-red-800 rounded-lg bg-red-50 dark:bg-red-900/20">
    <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">
      Error Loading Form
    </h3>
    <p className="text-red-600 dark:text-red-300 mb-4">
      {error.message || "An unexpected error occurred while loading the form."}
    </p>
    {onRetry && (
      <Button onClick={onRetry} variant="outline" size="sm">
        Try Again
      </Button>
    )}
  </div>
);

/**
 * Main DirectusForm component
 * @param {object} props - Component props
 * @param {object} props.client - Directus client instance
 * @param {string} props.collection - Name of the Directus collection
 * @param {string|number} [props.itemId=null] - ID of the item to edit (null for creating)
 * @param {object} [props.defaultValues={}] - Default values for the form fields
 * @param {number} [props.columns=2] - Number of columns in the form layout
 * @param {string} [props.spacing='normal'] - Spacing between form fields ('compact', 'normal', 'relaxed')
 * @param {boolean} [props.showTitle=true] - Whether to show the form title
 * @param {string} [props.title] - Custom form title
 * @param {string} [props.description] - Custom form description
 * @param {string} [props.submitText] - Custom text for the submit button
 * @param {string} [props.cancelText='Cancel'] - Custom text for the cancel button
 * @param {function} [props.onSuccess] - Callback function on successful submission
 * @param {function} [props.onError] - Callback function on submission error
 * @param {function} [props.onCancel] - Callback function when cancel button is clicked
 * @param {object} [props.fieldConfig={}] - Configuration to override individual field properties
 * @param {string[]} [props.excludeFields=[]] - List of field names to exclude from the form
 * @param {string[]} [props.includeFields=[]] - List of field names to include in the form
 * @param {string} [props.className=''] - Additional CSS classes for the form container
 * @returns {React.Component}
 */
export default function DirectusForm({
  client,
  collection,
  itemId = null,
  defaultValues = {},

  // Layout options
  columns = 2,
  spacing = "normal", // 'compact', 'normal', 'relaxed'

  // Display options
  showTitle = true,
  title,
  description,
  submitText,
  cancelText = "Cancel",

  // Event handlers
  onSuccess,
  onError,
  onCancel,

  // Custom field configuration
  fieldConfig = {},
  excludeFields = [],
  includeFields = [],
  readonlyFields = [],

  // Styling
  className = "",

  ...props
}) {
  // Using shared PortalMount component; no local hook needed

  const { form, fields, loading, submitting, error, handleSubmit, isEditing } =
    useDirectusForm({
      client,
      collection,
      itemId,
      defaultValues,
      onSuccess,
      onError,
      excludeFields,
      includeFields,
    });

  // Filter fields based on include/exclude lists
  const filteredFields = fields.filter((field) => {
    if (includeFields.length > 0 && !includeFields.includes(field.name)) {
      return false;
    }
    if (excludeFields.includes(field.name)) {
      return false;
    }
    return true;
  });

  // Apply custom field configuration
  const configuredFields = filteredFields.map((field) => ({
    ...field,
    ...fieldConfig[field.name],
  }));

  // Determine form title
  const formTitle = title
    ? isEditing
      ? `Edit ${title}`
      : `Add ${title}`
    : `${isEditing ? "Edit" : "Create"} ${collection}`;

  // Determine submit button text
  const buttonText = submitText || (isEditing ? "Update" : "Create");

  // Spacing classes
  const spacingClasses = {
    compact: "space-y-4",
    normal: "space-y-6",
    relaxed: "space-y-8",
  };

  const columnClasses = {
    full: 2,
    half: 1,
  };

  if (loading) {
    return (
      <div
        className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6 ${className}`}
      >
        {showTitle && (
          <div className="mb-6">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 animate-pulse mb-2" />
            {description && (
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 animate-pulse" />
            )}
          </div>
        )}
        <FormSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6 ${className}`}
      >
        <FormError error={error} />
      </div>
    );
  }

  console.log(props);
  

  return (
    <div>
      <EvtHeader title={formTitle} />

      <div className={className} {...props}>
        {/* Form */}
        <form onSubmit={handleSubmit} className={spacingClasses[spacing]}>
          {/* Form Fields */}
          <div className={`grid gap-4 grid-cols-1 md:grid-cols-${columns}`}>
            {configuredFields.map((field) => {
              return (
                <div
                  key={field.name}
                  style={{
                    gridColumn: field.width
                      ? `span ${columnClasses[field.width]}`
                      : "span 2",
                  }}
                >
                  <DirectusFieldRenderer
                    formField={field}
                    control={form.control}
                    errors={form.formState.errors}
                    client={client}
                    readonlyFields={readonlyFields}
                    disabled={props?.disabled}
                  />
                </div>
              );
            })}
          </div>

          <PortalMount to={UI_ACTION_PORTAL_ID}>
            {/* Form Actions */}
            <div>
              {onCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onCancel}
                  disabled={submitting}
                >
                  {cancelText}
                </Button>
              )}

              <Button
                type="submit"
                disabled={submitting || !form.formState.isDirty || props?.disabled}
                // className="min-w-[120px]"
                onClick={handleSubmit}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
            </div>
          </PortalMount>
        </form>
      </div>
    </div>
  );
}
